import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import CapWidget from '../components/CapWidget';
import toast from 'react-hot-toast';

const LandSearchFlowPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, sendVerificationCode, verifyCode, isSendingCode, isVerifying } = useAuth();
  const emailLocale = (i18n.language || 'en').split('-')[0];
  
  // Flow states: 'registration', 'verification', 'areas', 'subscription', 'search'
  const [flowStep, setFlowStep] = useState('registration');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Check if user is already authenticated and has subscription
  useEffect(() => {
    if (isAuthenticated && user?.role === 'land_seeker') {
      checkSubscriptionStatus();
    }
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkSubscriptionStatus = async () => {
    try {
      // Rely on axios instance with Authorization pre-set by AuthContext
      const response = await authAPI.get('/auth/me');
      const userData = response.data?.data;
      console.log('LandSearchFlowPage - User data:', userData);
      if (userData?.has_active_subscription) {
        console.log('LandSearchFlowPage - User has active subscription, redirecting to /search');
        setSubscriptionStatus('active');
        navigate('/search');
      } else {
        console.log('LandSearchFlowPage - User has no active subscription, redirecting to /preview');
        setSubscriptionStatus('expired');
        navigate('/preview');
      }
    } catch (error) {
      console.error('LandSearchFlowPage - Error checking subscription:', error);
      setSubscriptionStatus('none');
      navigate('/preview');
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    
    if (!captchaToken) {
      toast.error(t('auth.completeCaptcha'));
      return;
    }

    if (!email) {
      toast.error(t('auth.enterEmail'));
      return;
    }

    try {
      await sendVerificationCode({ email, role: 'land_seeker', captcha: captchaToken, locale: emailLocale });
      setFlowStep('verification');
      toast.success(t('auth.codeSent'));
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error(error.message || t('loginPage.failedToSendCode'));
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('auth.enterValidCode'));
      return;
    }

    try {
      await verifyCode({ email, verificationCode });
      toast.success(t('landSearchFlowPage.registrationSuccess'));
      
      // After successful verification, check subscription status
      setTimeout(() => {
        checkSubscriptionStatus();
      }, 1000);
      
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error(error.message || t('auth.invalidCode'));
    }
  };


  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  // Show loading while checking subscription status
  if (isAuthenticated && user?.role === 'land_seeker' && subscriptionStatus === null) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('landSearchFlowPage.checkingSubscriptionTitle')}</h2>
          <p className="text-gray-600">{t('landSearchFlowPage.checkingSubscriptionBody')}</p>
        </div>
      </div>
    );
  }

  // Registration Step
  if (flowStep === 'registration') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">🔍 {t('landSearchFlowPage.title')}</h1>
            <p className="text-gray-600">{t('landSearchFlowPage.subtitle')}</p>
          </div>

          <form onSubmit={handleRegistration} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('loginPage.emailPlaceholder')}
                required
              />
            </div>

            <div className="flex justify-center">
              <CapWidget
                siteKey="salesign-demo"
                onVerify={onCaptchaChange}
                onError={(error) => {
                  console.error('Cap verification failed:', error);
                  setCaptchaToken(null);
                }}
                theme="light"
                size="normal"
              />
            </div>

            <button
              type="submit"
              disabled={isSendingCode || !captchaToken}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSendingCode ? t('loginPage.sending') : t('landSearchFlowPage.registerContinue')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ← {t('loginPage.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Verification Step
  if (flowStep === 'verification') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">📧 {t('landSearchFlowPage.verifyTitle')}</h1>
            <p className="text-gray-600">
              {t('landSearchFlowPage.sentCodeTo')} <strong>{email}</strong>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {t('loginPage.codeExpires')}
            </p>
          </div>

          <form onSubmit={handleVerification} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.verificationCode')}
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
                placeholder={t('loginPage.codePlaceholder')}
                maxLength="6"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isVerifying || verificationCode.length !== 6}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifying ? t('loginPage.verifying') : t('landSearchFlowPage.verifyContinue')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setFlowStep('registration')}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ← {t('landSearchFlowPage.backToRegistration')}
            </button>
          </div>
        </div>
      </div>
    );
  }


  return null;
};

export default LandSearchFlowPage;
